import _, { find, get, groupBy, isObject, keys, map, reduce } from 'lodash';
import { flowTypes, pageConfig } from '@/common/service/page-config';
import { isEmptyVal } from '@/utils';
import {
  BILLING_CYCLE,
  DEFAULT_FREE_TRIAL_DAYS,
  DEFAULT_MIN_HOST,
  planTypes,
  ZOOM_MEETING_PRO_MIN_LICENSE,
  zoomOneProMonthlyPlanCodes,
  zoomOneProYearlyPlanCodes,
} from '@/common/constants';
import { logger } from '@/utils/log';
import {
  computedCallingPlans,
  computedPbxBasePlans,
  computedPbxCallingPlans,
  computedPbxPlans,
  isZoomPhone,
  PBX_SUB_TYPES,
} from '@/common/service/computers/plans/pbx';
import { getPlansMap } from '@/common/product';
import { isZoneBundleContext } from '@/plans/ZOOM_MEETING/common/utils';
import { FLOW_TYPE_KEY, PLAN_TYPE_KEY } from '@/views/config/coupon/helper/constant';
import { zoneBundleUtils } from '@/plans/ZOOM_ONE_BUNDLE/utils';
import { getConsumptionCode } from '@/plans/EVENT_SAAS/helper/parse';
import { addToCart } from '@/common/service/flow/utils';

const zoneBundlePlanTypeList = [planTypes.ZOOM_ONE_BUNDLE, planTypes.ZOOM_UNITED, planTypes.ZONE_PRO_PLUS];

export function generatePlansMap(plans = [], key = 'plan_code') {
  if (!plans.length) {
    return null;
  }

  return reduce(plans, (pre, next) => {
    const planCode = next[key];
    planCode && (pre[planCode] = next);
    return pre;
  }, {});
}

/**
 *
 * @param discountInfo {PreloadDiscountMapBean}
 * @param planMapObject {PreloadPlanMapBean}
 * @return {{newDiscountPlanInfo: {}, groupDiscountInfoWithType: {}}}
 */
const formatPreloadDiscountInfo = (discountInfo = {}, planMapObject = {}) => {
  return reduce(keys(discountInfo), (res, planCode) => {
    const { newDiscountPlanInfo, groupDiscountInfoWithType } = res;
    const curPlan = get(planMapObject, planCode, {});
    const type = get(curPlan, 'type', '');
    const curDiscountInfo = get(discountInfo, planCode, {});
    const mergePlanWithDiscount = { ...curPlan, ...curDiscountInfo };
    newDiscountPlanInfo[planCode] = mergePlanWithDiscount;
    groupDiscountInfoWithType[type] = [...(groupDiscountInfoWithType[type] || []), mergePlanWithDiscount];

    return res;
  }, {
    newDiscountPlanInfo: {},
    groupDiscountInfoWithType: {},
  });
};

// For edit flow with init shopping cart
const loadDefaultPlansByCouponEdit = (
  preloadGroupDiscountInfoWithType = {},
  planList = [],
  boughtPlans = [],
  planType,
) => {
  // preload discountList with current edit plan
  const currentDiscountList = preloadGroupDiscountInfoWithType[planType];
  let {
    defaultPreloadDiscountList: newDiscountInfoList,
    initGroupWithBillingCycle,
  } = getDefaultDiscountPlanList(currentDiscountList);

  if (planType === planTypes.ZOOM_PHONE) {
    return loadDefaultPbxPlans({
      initGroupWithBillingCycle,
      boughtPlans,
      planList,
      defaultDiscountInfoList: newDiscountInfoList,
    });
  }

  if (isZoneBundleContext) {
    const pbxBasePlan = computedPbxPlans(boughtPlans).filter((item) => item.sub_type === PBX_SUB_TYPES.BASE);
    const categoryPlans = newDiscountInfoList.concat(pbxBasePlan);
    return categoryPlans;
  }

  return [get(newDiscountInfoList, '0')];
};

// coupon config have pay as you go base plan (come from campaign or planList)
function getPbxBasePlanWithConfig({
                                    defaultPayAsYouGoList = [],
                                    planList = [],
                                  }) {
  if (defaultPayAsYouGoList?.length) {
    logger.info('default pbx base come from coupon campaign config');
    return defaultPayAsYouGoList[0];
  }

  return _.find(computedPbxBasePlans(planList), (p) => p.is_payg);
}

const loadDefaultPbxPlans = ({
                               initGroupWithBillingCycle = {},
                               boughtPlans = [],
                               planList = [],
                               defaultDiscountInfoList = [],
                             }) => {
  function preloadSelectedPlansWithBoughtPbx() {
    const boughtPbxPlans = computedPbxPlans(boughtPlans);
    const boughtPbxCallingPlans = computedCallingPlans(boughtPbxPlans);

    const billingCycle = _.get(boughtPbxCallingPlans, [0, 'billing_cycle']);
    const preloadPlanList = initGroupWithBillingCycle[billingCycle] || defaultDiscountInfoList;

    return _.chain(preloadPlanList)
      .map((p) => _.find(boughtPbxPlans, (x) => p.plan_code === x.plan_code) || p)
      .concat(boughtPbxPlans)
      .filter(Boolean)
      .uniqBy((p) => p.plan_code)
      .value();
  }

  let newDiscountInfoList = defaultDiscountInfoList;

  if (flowTypes.edit === pageConfig?.type) {
    return preloadSelectedPlansWithBoughtPbx();
  }

  if (flowTypes.add2cart === pageConfig?.type) {
    const boughtPbxBasePlans = computedPbxBasePlans(boughtPlans);

    // for have bought pbx base plan case
    if (boughtPbxBasePlans?.length) {
      return preloadSelectedPlansWithBoughtPbx();
    }
  }

  const preloadBasePbxPlans = computedPbxBasePlans(newDiscountInfoList);

  // exist preload pbx base plan, do not need set pay as you go base plan to preloadPlanList
  if (!preloadBasePbxPlans?.length) {
    const defaultPayAsYouGoList = initGroupWithBillingCycle[BILLING_CYCLE.PAY_AS_YOU_GO];

    // for zoom phone
    const payAsYouGoBasePlan = getPbxBasePlanWithConfig({
      defaultPayAsYouGoList,
      planList,
    });

    if (payAsYouGoBasePlan) newDiscountInfoList.unshift(payAsYouGoBasePlan);
  }

  return newDiscountInfoList;
};

// for zoom one pro/biz plus/zoom united
const loadZoneBundlePlans = ({
                               initGroupWithBillingCycle = {},
                               boughtPlans = [],
                               planList = [],
                               monthlyDiscountList,
                               annualDiscountList,
                             }) => {
  const boughtPbxBasePlan = computedPbxBasePlans(boughtPlans)?.[0];
  // pbx base plan
  const defaultPbxBasePlan = boughtPbxBasePlan ?? getPbxBasePlanWithConfig({
    defaultPayAsYouGoList: initGroupWithBillingCycle[BILLING_CYCLE.PAY_AS_YOU_GO],
    planList,
  });

  const pbxBillingCycle = _.get(computedCallingPlans(boughtPlans), [0, 'billing_cycle']);

  let newDiscountInfoList = initGroupWithBillingCycle[pbxBillingCycle] || monthlyDiscountList || annualDiscountList || [];

  if (defaultPbxBasePlan) newDiscountInfoList.unshift(defaultPbxBasePlan);

  return newDiscountInfoList;
};

// For buyNow/free2paid/add2cart flow with init shopping cart
const loadDefaultPlansByCouponNormal = (
  preloadGroupDiscountInfoWithType = {},
  planList = [],
  boughtPlans = [],
) => {
  const planTypeList = keys(preloadGroupDiscountInfoWithType);
  const promoContainZoneBundle = _.some(zoneBundlePlanTypeList, (p) => _.includes(planTypeList, p));

  return reduce(planTypeList, (pre, type) => {
    const currentDiscountList = preloadGroupDiscountInfoWithType[type];
    let {
      defaultPreloadDiscountList: newDiscountInfoList,
      initGroupWithBillingCycle,
      monthlyDiscountList,
      annualDiscountList,
    } = getDefaultDiscountPlanList(currentDiscountList);

    // zoom one biz plus/zoom one pro plus
    if (promoContainZoneBundle) {
      // just for zoom one pro/biz plus (add2cart flow), do not add pbx default plans
      if (flowTypes.add2cart === pageConfig?.type && type === planTypes.ZOOM_PHONE) {
        return pre;
      }

      if (_.includes(zoneBundlePlanTypeList, type)) {
        newDiscountInfoList = loadZoneBundlePlans({
          initGroupWithBillingCycle,
          boughtPlans,
          planList,
          monthlyDiscountList,
          annualDiscountList,
        });
      }
    }

    // for zoom phone
    if (type === planTypes.ZOOM_PHONE) {
      newDiscountInfoList = loadDefaultPbxPlans({
        initGroupWithBillingCycle,
        planList,
        boughtPlans,
        defaultDiscountInfoList: newDiscountInfoList,
      });
    }

    return [...pre, ...newDiscountInfoList];
  }, []);
};

// batch update shopping cart
const batchChangePlanList = (planList = [], preloadPlanList = [], defaultQuantity = DEFAULT_MIN_HOST) => {
  const preloadPlanMap = generatePlansMap(preloadPlanList);

  // forEach planList and change cart with preload discount plan
  return planList.map((plan) => {
    const planCode = plan.plan_code;
    const preloadPlan = get(preloadPlanMap, planCode);

    if (preloadPlan) {
      const pbxBasePlanList = computedPbxBasePlans([preloadPlan]);
      let selectedNumber = pbxBasePlanList?.length ? DEFAULT_MIN_HOST :
        preloadPlan?.cartInfo?.selected_number || preloadPlan?.preloadQuantity || defaultQuantity;

      if ((
        [planTypes.ZOOM_ONE_BUNDLE, planTypes.ZOOM_UNITED].includes(preloadPlan?.type)
        || planCode.indexOf('business') !== -1)
        && selectedNumber < ZOOM_MEETING_PRO_MIN_LICENSE
      ) {
        selectedNumber = ZOOM_MEETING_PRO_MIN_LICENSE;
      }

      return {
        ...plan,
        cartInfo: { selected_number: selectedNumber },
      };
    }

    return plan;
  });
};

// get default discount list with billing cycle
const getDefaultDiscountPlanList = (currentDiscountList = []) => {
  // groupBy discountList with billingCycle
  const initGroupWithBillingCycle = groupBy(currentDiscountList, 'billing_cycle');
  const monthlyDiscountList = initGroupWithBillingCycle[BILLING_CYCLE.MONTHLY]
    || initGroupWithBillingCycle[BILLING_CYCLE.ONE_MONTH]
    || initGroupWithBillingCycle[BILLING_CYCLE.ONE_TIME];
  const annualDiscountList = initGroupWithBillingCycle[BILLING_CYCLE.ANNUAL];
  return {
    defaultPreloadDiscountList: monthlyDiscountList || annualDiscountList || [],
    monthlyDiscountList,
    annualDiscountList,
    initGroupWithBillingCycle,
  };
};

/**
 * @description parse marketing coupon
 * @param planList {PlanItemBean[]}
 * @param marketingCoupon {PreloadCouponBean}
 * @return {{newDiscountPlanInfo: {}, defaultNewDiscountPlanInfo: {}, groupDiscountInfoWithType: {}, defaultGroupDiscountInfoWithType: {}, preloadNewDiscountPlanInfo: {}, multipleTargetSwitch: *, preloadGroupDiscountInfoWithType: {}}}
 */
const getParsePayloadFromMarketingCoupon = (planList = [], marketingCoupon = {}) => {
  const { discountInfo, defaultDiscountInfo, multipleTargetSwitch } = marketingCoupon;

  const planMapObject = generatePlansMap(planList);
  // newDiscountInfo contains { groupDiscountInfoWithType, newDiscountPlanInfo }
  const newDiscountInfo = formatPreloadDiscountInfo(discountInfo, planMapObject);
  // newDiscountPlanInfo|newDefaultDiscountInfo = discountInfo with expanded all fields of plan
  const newDefaultDiscountInfo = formatPreloadDiscountInfo(defaultDiscountInfo, planMapObject);
  // Discount classification by planType
  const { groupDiscountInfoWithType, newDiscountPlanInfo } = newDiscountInfo;
  const { groupDiscountInfoWithType: defaultGroupDiscountInfoWithType, newDiscountPlanInfo: defaultNewDiscountPlanInfo } = newDefaultDiscountInfo;
  const useDefault = multipleTargetSwitch && !isEmptyVal(defaultGroupDiscountInfoWithType);

  const preloadGroupDiscountInfoWithType = useDefault ? defaultGroupDiscountInfoWithType : groupDiscountInfoWithType; // key -> planType, value -> discountPlan list
  const preloadNewDiscountPlanInfo = useDefault ? defaultNewDiscountPlanInfo : newDiscountPlanInfo; // key -> planCode, value -> discount Info

  return {
    newDiscountPlanInfo,
    defaultNewDiscountPlanInfo,
    groupDiscountInfoWithType,
    defaultGroupDiscountInfoWithType,
    multipleTargetSwitch,
    preloadGroupDiscountInfoWithType,
    preloadNewDiscountPlanInfo,
  };
};

/**
 *
 * @param boughtPlans (not required)
 * @param marketingCouponParams (not required)
 * @returns {(function([]=): (((*&{cartInfo: {selected_number: number}})|*)[]))|*}
 */
// BUY_NOW/FREE2PAID/ADD2CART FLOW preload plans
export const loadDefaultPlansByPreloadCoupon = (boughtPlans = [], marketingCouponParams) => {
  return (planList = []) => {
    const marketingCoupon = marketingCouponParams ?? pageConfig?.marketingCoupon;
    const haveSelectedPlan = find(planList, (p) => p.cartInfo?.selected_number);

    if (!haveSelectedPlan && marketingCoupon?.discountInfo) {
      const { preloadGroupDiscountInfoWithType } = getParsePayloadFromMarketingCoupon(planList, marketingCoupon);

      const payload = [preloadGroupDiscountInfoWithType, planList, boughtPlans];

      const preloadPlanList = loadDefaultPlansByCouponNormal(...payload);

      logger.info('preLoadWithPreloadCoupon', preloadPlanList);

      return batchChangePlanList(planList, preloadPlanList);
    }

    return planList;
  };
};

export const loadDefaultPlansByPreloadCouponForEdit = (boughtPlans = []) => {
  return (planList = []) => {
    const haveSelectedPlan = find(planList, (p) => p.cartInfo?.selected_number);

    if (haveSelectedPlan) return planList;

    const marketingCoupon = pageConfig?.marketingCoupon;

    if (marketingCoupon && !_.isString(marketingCoupon)) {
      return loadDefaultPlansByPreloadForEdit(planList, boughtPlans, marketingCoupon);
    }

    return planList;
  }
}

export const getCurrentEditPlanQuantity = (boughtPlans) => {
  const editPlanCode = pageConfig?.editConfig.oldPlanCode;
  const editPlan = find(boughtPlans, (p) => p.plan_code === editPlanCode);
  return get(editPlan, 'cartInfo.selected_number', DEFAULT_MIN_HOST);
}

// EDIT FLOW preload plans
export const loadDefaultPlansByPreloadForEdit = (planList = [], boughtPlans = [], marketingCoupon) => {
  if (marketingCoupon?.discountInfo) {
    const editSelectedNumber = getCurrentEditPlanQuantity(boughtPlans);

    const { preloadGroupDiscountInfoWithType } = getParsePayloadFromMarketingCoupon(planList, marketingCoupon);
    const planType = pageConfig?.editConfig?.planType;

    const payload = [preloadGroupDiscountInfoWithType, planList, boughtPlans, planType];

    const preloadPlanList = loadDefaultPlansByCouponEdit(...payload);

    logger.info('preLoadWithPreloadCoupon', preloadPlanList);

    return batchChangePlanList(planList, preloadPlanList, editSelectedNumber);
  }

  return planList;
};

export const loadDefaultPlansByPreloadCoupon4MixBuy = (boughtPlans) => {
  const marketingCoupon = pageConfig?.marketingCoupon;

  if (marketingCoupon && !_.isString(marketingCoupon)) {
    return loadDefaultPlansByPreload4MixBuy(boughtPlans, marketingCoupon);
  }

  return (planList) => planList;
};

// MIX_BUY FLOW preload plans
export const loadDefaultPlansByPreload4MixBuy = (boughtPlans = [], marketingCoupon) => {
  return (planList = []) => {
    const haveSelectedPlan = find(planList, (p) => p.cartInfo?.selected_number);

    if (!haveSelectedPlan && marketingCoupon?.discountInfo) {
      // Object key -> planType, value -> bought paid planList
      const boughtPaidPlan4PlanType = _.chain(boughtPlans)
        .filter((p) => !p.isFreeTrial)
        .groupBy(PLAN_TYPE_KEY)
        .value();

      // Object key -> planCode, value -> plan
      const { preloadNewDiscountPlanInfo } = getParsePayloadFromMarketingCoupon(planList, marketingCoupon);
      // Object key -> flowType, value -> planList
      const preloadGroupDiscountInfoWithFlowType = _.chain(preloadNewDiscountPlanInfo)
        .values()
        .groupBy(FLOW_TYPE_KEY)
        .value();

      const preloadPlanList = _.chain(preloadGroupDiscountInfoWithFlowType)
        .keys()
        .reduce((pre, flowType) => {
          // get current discount plan list by flowType
          const curPreloadGroupDiscountInfo = _.get(preloadGroupDiscountInfoWithFlowType, [flowType]);

          // for promotion edit flow type
          if (flowType === flowTypes.edit) {
            // Object key -> planType, value -> planList
            const preloadGroupDiscountInfoWithType = _.chain(curPreloadGroupDiscountInfo)
              .map((p) => {
                const quantity = _.get(boughtPaidPlan4PlanType, [p?.type, 0, 'cartInfo', 'selected_number']);
                return { ...p, preloadQuantity: quantity };
              })
              .groupBy(PLAN_TYPE_KEY)
              .value();

            const preloadPlans = _.chain(preloadGroupDiscountInfoWithType)
              .keys()
              .reduce((p, planType) => {
                const payload = [preloadGroupDiscountInfoWithType, planList, boughtPlans, planType];
                const plans = loadDefaultPlansByCouponEdit(...payload);
                return [...p, ...plans];
              }, [])
              .value();

            return [...pre, ...preloadPlans];
          }

          // for promotion add addon flow type
          if (flowType === flowTypes.add2cart) {
            // Object key -> planType, value -> planList
            const preloadGroupDiscountInfoWithType = _.groupBy(curPreloadGroupDiscountInfo, PLAN_TYPE_KEY);
            const payload = [preloadGroupDiscountInfoWithType, planList, boughtPlans];
            const preloadPlans = loadDefaultPlansByCouponNormal(...payload);

            return [...pre, ...preloadPlans];
          }

          return pre;
        }, [])
        .value();

      logger.info('preLoadWithPreloadCoupon', preloadPlanList);

      return batchChangePlanList(planList, preloadPlanList);
    }

    return planList;
  };
};

// pre load with share shopping card id or partner coupon (cartId or referId)
// RENDER WITH CART_ID
export const loadDefaultPlansByCartId = (planList) => {
  const ppaPlans = planList.filter(item => item.isPPA);
  const haveSelectedPlan = find(planList, (p) => p.cartInfo?.selected_number);

  /** @type {PreloadWithCartIdResultDto} */
  const preloadCartInfo = window.$config?.('preloadCartInfo');

  if (!haveSelectedPlan && preloadCartInfo) {
    const planMap = reduce(preloadCartInfo?.orderData?.planList, (pre, item) => {
      const isPPAPlan = !!ppaPlans.find(ppa => ppa.originPlanCode && ppa.originPlanCode === item.planCode);

      const quantity = isPPAPlan ? 1 : item.quantity;
      const currentPlanCode = isPPAPlan ? getConsumptionCode(item.planCode, item.quantity) : item.planCode;
      const isZoomOneProMonthly = zoomOneProMonthlyPlanCodes.includes(currentPlanCode);

      // zoom one pro monthly should consider increase pricing and standard pricing
      if (isZoomOneProMonthly) {
        return reduce(zoomOneProMonthlyPlanCodes, (prev, planCode) => {
          return { ...prev, [planCode]: quantity };
        }, pre);
      }

      const isZoomOneProYearly = zoomOneProYearlyPlanCodes.includes(currentPlanCode);
      // PRO INCREASE -
      // zoom one pro yearly should consider increase pricing and standard pricing
      if (isZoomOneProYearly) {
        return reduce(zoomOneProYearlyPlanCodes, (prev, planCode) => {
          return { ...prev, [planCode]: quantity };
        }, pre);
      }

      // If free trial plan, keep planExtension for later use
      if (item.planExtension && item.planExtension.trialCode) {
        return { ...pre, [currentPlanCode]: item.planExtension }
      }

      return { ...pre, [currentPlanCode]: quantity };
    }, {});


    console.log('ppaPlans: planMap', planMap)

    return map(planList, (p) => {
      const cartPlanInfo = get(planMap, p.plan_code);

      if (!isEmptyVal(cartPlanInfo)) {
        if (typeof cartPlanInfo === 'number') {
          p.cartInfo.selected_number = cartPlanInfo;
        } else if (isObject(cartPlanInfo)) {
          // for free trial
          p.cartInfo.selected_number = 1
          p.trialDays = cartPlanInfo.trialDays || DEFAULT_FREE_TRIAL_DAYS
          p.trialCode = cartPlanInfo.trialCode || ''
          p.campaignCode = cartPlanInfo.campaignCode || ''
          p.isOnlineFreeTrial = true
        }
      }

      return p;
    });
  }

  return planList;
};

export const disabledBoughtPlansWithCampaign = (subscriptions = [], boughtPlans = []) => {
  const fromCampaign = !isEmptyVal(pageConfig?.marketingCoupon);
  const editPlanType = pageConfig?.editConfig.planType;

  // when the customer bought pbx plans, campaign pbx plan, should disabled purchased pbx plan
  if (fromCampaign && editPlanType === planTypes.ZOOM_PHONE) {
    const boughtPlansMap = getPlansMap(boughtPlans);

    return _.map(subscriptions, (p) => {
      const item = _.get(boughtPlansMap, p.plan_code, {});

      return isZoomPhone(item?.type) ? {
        ...p,
        should_disabled: true,
      } : p;
    });
  }

  return subscriptions;
};

// Upgrade FLOW preload plans Start
const loadDefaultPlansByCouponUpgrade = (
  preloadGroupDiscountInfoWithType = {},
  planList = [],
  boughtPlans = [],
) => {
  const upgradePlanType = pageConfig?.editConfig?.planType;
  // preload discountList with will upgrade plan
  const currentDiscountList = preloadGroupDiscountInfoWithType[upgradePlanType];

  const {
    defaultPreloadDiscountList: newDiscountInfoList,
    initGroupWithBillingCycle,
    monthlyDiscountList,
    annualDiscountList,
  } = getDefaultDiscountPlanList(currentDiscountList);

  // For zone pro+/biz+ etc...
  if (zoneBundleUtils.isZoneBundleByPlanType(upgradePlanType)) {
    return loadZoneBundlePlans({
      initGroupWithBillingCycle,
      boughtPlans,
      planList,
      monthlyDiscountList,
      annualDiscountList,
    });
  }

  return [get(newDiscountInfoList, '0')];
};

export const loadDefaultPlansByPreloadCouponForUpgrade = (boughtPlans = [], sourceTargetPlan = {}) => {
  return (planList = []) => {
    const marketingCoupon = pageConfig?.marketingCoupon;
    const haveSelectedPlan = find(planList, (p) => p.cartInfo?.selected_number);

    if (!haveSelectedPlan && marketingCoupon?.discountInfo) {
      const editSelectedNumber = get(sourceTargetPlan, 'cartInfo.selected_number', DEFAULT_MIN_HOST);

      const { preloadGroupDiscountInfoWithType } = getParsePayloadFromMarketingCoupon(planList, marketingCoupon);

      const payload = [preloadGroupDiscountInfoWithType, planList, boughtPlans];

      const preloadPlanList = loadDefaultPlansByCouponUpgrade(...payload);

      logger.info('preLoadWithPreloadCoupon', preloadPlanList);

      return batchChangePlanList(planList, preloadPlanList, editSelectedNumber);
    }

    return planList;
  };
};

// deal with marketing campaign for expired coupon without coupon meta
export const preloadWithNoActiveCouponCampaignUtils = {
  [planTypes.ZOOM_PHONE]: (planList = []) => {
    const callingPlans = computedPbxCallingPlans(planList);
    const defaultCallingPlan = _.get(callingPlans, [0]);
    const pbxBasePlans = computedPbxBasePlans(planList);
    const defaultPbxPayAsYouGoPlan = _.find(pbxBasePlans, (p) => p.is_payg);
    [defaultCallingPlan, defaultPbxPayAsYouGoPlan].filter(Boolean).forEach((p) => addToCart(p));
    return planList;
  }
}
// Upgrade FLOW preload plans End
